'use client';

import Text, { TEXT_VARIANT } from '@pixum/text';
import type { CSSProperties } from 'react';
import type { LinkListProps } from './types';
import type { LinkNode } from 'data-layer/src/lib/transformers/types';
import { LogoLink } from './LogoLink';
import { IconMedia } from '../../../icons';
import { CREATIVE_ID, useAppAdjustLink } from 'hooks';

/**
 * Renders a single app store link item.
 *
 * Extracted from AppLinkList to satisfy React's Rules of Hooks — hooks must not
 * be called inside loops or map callbacks. Each item has its own useAppAdjustLink
 * call at the top level of this component.
 */
function AppLinkListItem({ link, operatingSystem }: { link: LinkNode; operatingSystem: string | undefined }) {
  const appAdjustUrl = useAppAdjustLink(link.url, CREATIVE_ID.FOOTER) as string;

  // Strip locale suffix (e.g. appstore_apple-fr → appstore_apple) for CSS-based OS filtering.
  // The full locale-suffixed value still flows into LogoLink for locale-specific image resolution.
  const baseIcon = link.icon.replace(/-[a-z]{2}$/, '');

  return (
    <li className="footer__links-list-item" data-app-store={baseIcon} data-os={operatingSystem}>
      <LogoLink {...link} addHref={true} url={appAdjustUrl || link.url} />
    </li>
  );
}

/**
 * AppLinkList Component
 *
 * Renders a list of app store links (Apple App Store, Google Play, Huawei AppGallery)
 * with CSS-based OS filtering via data-os / data-app-store attributes.
 *
 * On mobile, only the store relevant to the user's OS is shown. The OS is detected
 * server-side (via the x-detected-os middleware header) and passed as
 * initialOperatingSystem by SoftwareLinksServer to avoid an SSR/hydration mismatch.
 * When not provided (Suspense fallback, bots, macOS), data-os is not rendered so all
 * store buttons remain visible — correct for crawlers and no-JS environments.
 */
export function AppLinkList(props: LinkListProps) {
  const { align, direction, flow, links, title, type, initialOperatingSystem } = props;
  const customProperties = {
    '--footer-links-list-item-align': align,
    '--footer-links-list-item-direction': direction,
    '--footer-links-list-item-flow': flow
  } as CSSProperties;

  const operatingSystem = initialOperatingSystem;

  return (
    <section className="footer__links-list-section variant-app" id={type}>
      <Text className="footer__links-list-heading" variant={TEXT_VARIANT.HEADLINE}>
        {title}
      </Text>
      <ul className="footer__links-list" style={customProperties}>
        <li className="footer__links-list-item">
          <IconMedia alt="phone-icon" icon="phone-dark" loading="lazy" />
        </li>
        {links.map((link, index) => (
          <AppLinkListItem key={`footer-logo-link-${type}-${index}`} link={link} operatingSystem={operatingSystem} />
        ))}
      </ul>
    </section>
  );
}
